import { HomeAgencySummary } from '../features/agency/HomeAgencySummary';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAdminDashboardOverview } from '../features/dashboard';
import type {
  ActiveDownloadTaskDTO,
  DashboardPendingDownloadReviewDTO,
  DashboardPendingFeedbackDTO,
  DashboardPendingWithdrawalDTO,
  DashboardRecentUserDTO,
} from '../features/dashboard';
import { formatDateTime } from '../lib/format';

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  all: '全平台',
};

const FEEDBACK_CATEGORY_LABELS: Record<string, string> = {
  task: '任务问题',
  withdrawal: '提现问题',
  account: '账号问题',
  bug: 'Bug 反馈',
  suggestion: '产品建议',
  other: '其他问题',
};

function formatPercent(value: number, total: number): string {
  if (total <= 0) return '0%';
  return `${((value / total) * 100).toFixed(1)}%`;
}

function formatShortTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function toneClass(tone: 'slate' | 'orange' | 'emerald' | 'blue' | 'rose') {
  return {
    slate: 'border-gray-200 bg-white text-gray-900',
    orange: 'border-orange-200 bg-orange-50/70 text-orange-900',
    emerald: 'border-emerald-200 bg-emerald-50/80 text-emerald-900',
    blue: 'border-sky-200 bg-sky-50/80 text-sky-900',
    rose: 'border-rose-200 bg-rose-50/80 text-rose-900',
  }[tone];
}

function PanelShell({
  title,
  subtitle,
  action,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm shadow-gray-100 ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function MetricTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: 'slate' | 'orange' | 'emerald' | 'blue' | 'rose';
}) {
  return (
    <div className={`rounded-[20px] border p-4 ${toneClass(tone)}`}>
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-gray-500">{label}</p>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="text-3xl font-semibold tabular-nums">{value}</div>
      </div>
      <p className="mt-3 text-sm text-gray-600">{note}</p>
    </div>
  );
}

function DownloadPackageCard({
  platform,
  version,
  fileSize,
  href,
  ready,
}: {
  platform: string;
  version: string;
  fileSize: string;
  href?: string;
  ready: boolean;
}) {
  return (
    <div className="rounded-[22px] border border-gray-200 bg-white/90 p-5 shadow-sm shadow-gray-100">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-gray-400">{platform}</p>
          <div className="mt-3 text-2xl font-semibold text-gray-900">{ready ? version : '待上传'}</div>
          <p className="mt-2 text-sm text-gray-500">{ready ? `当前分发包体积 ${fileSize}` : '当前还没有可公开分发的安装包'}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${ready ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {ready ? '可下载' : '待补充'}
        </span>
      </div>
      <div className="mt-6">
        {ready && href ? (
          <a
            href={href}
            download
            className="inline-flex h-10 items-center rounded-full bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            立即下载
          </a>
        ) : (
          <span className="inline-flex h-10 items-center rounded-full border border-gray-200 px-4 text-sm text-gray-400">
            等待上传
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-[18px] border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
      {text}
    </div>
  );
}

function ActiveTaskRow({ task }: { task: ActiveDownloadTaskDTO }) {
  const progress = task.max_assignments > 0 ? Math.min(100, Math.round((task.completed_count / task.max_assignments) * 100)) : 0;
  return (
    <div className="rounded-[18px] border border-gray-100 bg-gray-50/70 p-4">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200">
          {task.icon_url ? (
            <img src={task.icon_url} alt={task.app_name || task.title} className="h-full w-full object-cover" />
          ) : (
            <span className="text-sm font-semibold text-gray-400">{PLATFORM_LABELS[task.platform] ?? 'App'}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">{task.app_name || task.title}</p>
              <p className="mt-1 text-xs text-gray-500">
                {PLATFORM_LABELS[task.platform] ?? task.platform}
                {task.app_version ? ` · ${task.app_version}` : ''}
                {` · 奖励 ¥${task.reward_amount_yuan}`}
              </p>
            </div>
            <div className="text-right text-xs text-gray-500">
              <div className="font-medium text-gray-900 tabular-nums">{progress}%</div>
              <div>完成率</div>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,#f06b52,#f6a04d)]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span>完成 {task.completed_count}/{task.max_assignments || 0}</span>
            <span>当前领取 {task.current_assigned}</span>
            <span>{formatShortTime(task.updated_at)} 更新</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DownloadReviewItem({ item }: { item: DashboardPendingDownloadReviewDTO }) {
  return (
    <Link
      to={`/assignments/${item.assignment_id}`}
      className="flex items-center justify-between gap-3 rounded-[16px] border border-gray-100 bg-gray-50/70 px-4 py-3 transition-colors hover:border-primary/20 hover:bg-orange-50/50"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-gray-900">{item.task_title}</div>
        <div className="mt-1 text-xs text-gray-500">
          用户 #{item.user_id} {item.user_nickname || '未命名用户'} · {PLATFORM_LABELS[item.platform] ?? item.platform}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-gray-500">
        <div>{formatShortTime(item.submitted_at)}</div>
        <div className="mt-1 text-primary">查看</div>
      </div>
    </Link>
  );
}

function RecentUserRow({ item }: { item: DashboardRecentUserDTO }) {
  return (
    <Link
      to={`/users/${item.id}`}
      className="flex items-center justify-between gap-3 rounded-[16px] border border-gray-100 bg-gray-50/70 px-4 py-3 transition-colors hover:border-primary/20 hover:bg-orange-50/40"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">{item.nickname || `用户 #${item.id}`}</span>
          {item.apple_verification_status === 'verified' ? (
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">Apple 已验证</span>
          ) : null}
          {item.risk_tagged ? (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">风险命中</span>
          ) : null}
        </div>
        <div className="mt-1 truncate text-xs text-gray-500">{item.email || '未填写邮箱'}</div>
      </div>
      <div className="shrink-0 text-xs text-gray-500">{formatShortTime(item.created_at)}</div>
    </Link>
  );
}

function PendingWithdrawalRow({ item }: { item: DashboardPendingWithdrawalDTO }) {
  return (
    <Link
      to="/withdrawals"
      className="flex items-center justify-between gap-3 rounded-[16px] border border-gray-100 bg-gray-50/70 px-4 py-3 transition-colors hover:border-primary/20 hover:bg-orange-50/40"
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{item.nickname || `用户 #${item.user_id}`}</div>
        <div className="mt-1 text-xs text-gray-500">提现申请 #{item.id}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-semibold tabular-nums text-gray-900">¥{item.amount_yuan}</div>
        <div className="mt-1 text-xs text-gray-500">{formatShortTime(item.created_at)}</div>
      </div>
    </Link>
  );
}

function PendingFeedbackRow({ item }: { item: DashboardPendingFeedbackDTO }) {
  return (
    <Link
      to="/support-feedback"
      className="flex items-center justify-between gap-3 rounded-[16px] border border-gray-100 bg-gray-50/70 px-4 py-3 transition-colors hover:border-primary/20 hover:bg-orange-50/40"
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{item.nickname || `用户 #${item.user_id}`}</div>
        <div className="mt-1 text-xs text-gray-500">{FEEDBACK_CATEGORY_LABELS[item.category] ?? item.category}</div>
      </div>
      <div className="shrink-0 text-xs text-gray-500">{formatShortTime(item.created_at)}</div>
    </Link>
  );
}

function LoadingPanel() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="h-48 animate-pulse rounded-[24px] bg-gray-100" />
        <div className="h-48 animate-pulse rounded-[24px] bg-gray-100" />
      </div>
      <div className="h-64 animate-pulse rounded-[28px] bg-gray-100" />
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="h-96 animate-pulse rounded-[24px] bg-gray-100" />
        <div className="h-96 animate-pulse rounded-[24px] bg-gray-100" />
      </div>
    </div>
  );
}

export function HomePage() {
  const { data, loading, error, reload } = useAdminDashboardOverview();

  const summary = data?.summary;
  const backlogTotal = summary ? summary.pending_download_reviews + summary.pending_withdrawals + summary.pending_feedbacks : 0;
  const reviewSuccessTotal = summary ? summary.approved_download_reviews + summary.settled_download_reviews : 0;
  const reviewFunnel = summary
    ? [
        { label: '待审核', value: summary.pending_download_reviews, color: 'bg-orange-500' },
        { label: '已通过', value: summary.approved_download_reviews, color: 'bg-sky-500' },
        { label: '已结算', value: summary.settled_download_reviews, color: 'bg-emerald-500' },
        { label: '已拒绝', value: summary.rejected_download_reviews, color: 'bg-rose-500' },
      ]
    : [];
  const maxFunnelValue = Math.max(...reviewFunnel.map((item) => item.value), 1);

  return (
    <div className="space-y-6">
      <HomeAgencySummary />
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">首页总览</h1>
          <p className="mt-1 text-sm text-gray-500">围绕下载任务、用户验证、提现审核与客服反馈的实时运营面板。</p>
        </div>
        {data ? (
          <div className="text-xs text-gray-400">最近刷新：{formatDateTime(data.generated_at)}</div>
        ) : null}
      </div>

      <section className="rounded-[28px] border border-orange-100 bg-[radial-gradient(circle_at_top_left,_rgba(240,107,82,0.14),_transparent_35%),linear-gradient(180deg,_#fffefc,_#fff7f2)] p-6 shadow-sm shadow-orange-100/40">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary/80">Downloads & Operations</p>
            <h2 className="mt-2 text-2xl font-semibold text-gray-900">安装包分发与运营节奏放在同一屏里</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
              上方继续保留安装包公开分发入口，下方直接展示真实线上数据，首页打开就能看到今天该优先处理的审核积压与业务状态。
            </p>
          </div>
          <button
            onClick={() => void reload()}
            className="inline-flex h-10 items-center justify-center rounded-full border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:border-primary/20 hover:text-primary"
          >
            刷新数据
          </button>
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          <DownloadPackageCard
            platform="Android"
            version="v1.0.0"
            fileSize="约 105 MB"
            href="/downloads/mobile/aiptyg-android-latest.apk"
            ready
          />
          <DownloadPackageCard
            platform="iOS"
            version="v1.0.0 Release"
            fileSize="约 20 MB"
            href="/downloads/mobile/aiptyg-ios-latest.ipa"
            ready
          />
        </div>
      </section>

      {loading && !data ? <LoadingPanel /> : null}

      {error ? (
        <div className="rounded-[24px] border border-red-200 bg-red-50 px-6 py-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => void reload()} className="text-sm font-medium text-red-700 hover:underline">
              重新加载
            </button>
          </div>
        </div>
      ) : null}

      {data && summary ? (
        <>
          <section className="rounded-[28px] border border-gray-200 bg-[radial-gradient(circle_at_top_right,_rgba(240,107,82,0.12),_transparent_32%),linear-gradient(180deg,_#ffffff,_#fffaf7)] p-6 shadow-sm shadow-gray-100">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-gray-400">Operations Pulse</p>
                <h2 className="mt-2 text-2xl font-semibold text-gray-900">业务总览与待办压力</h2>
                <p className="mt-2 text-sm text-gray-500">
                  核心指标均来自线上真实表数据，聚焦新增、验证、审核、提现与客服积压。
                </p>
              </div>
              <div className="rounded-full border border-gray-200 bg-white px-4 py-2 text-xs text-gray-500">
                审核通过口径：已通过 {summary.approved_download_reviews} · 已结算 {summary.settled_download_reviews}
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <MetricTile
                label="注册用户"
                value={summary.total_users}
                note={`今日新增 ${summary.new_users_today} 人`}
                tone="slate"
              />
              <MetricTile
                label="在线人数"
                value={summary.online_users ?? 0}
                note="最近 15 分钟活跃"
                tone="emerald"
              />
              <MetricTile
                label="Apple 验证"
                value={summary.verified_users}
                note={`验证覆盖率 ${formatPercent(summary.verified_users, summary.total_users)}`}
                tone="blue"
              />
              <MetricTile
                label="下载审核"
                value={summary.pending_download_reviews}
                note={`活跃下载任务 ${summary.active_download_tasks} 个`}
                tone="orange"
              />
              <MetricTile
                label="运营积压"
                value={backlogTotal}
                note={`提现 ${summary.pending_withdrawals} · 反馈 ${summary.pending_feedbacks}`}
                tone={backlogTotal > 0 ? 'rose' : 'emerald'}
              />
            </div>

            <div className="mt-4 grid gap-3 xl:grid-cols-3">
              <div className="rounded-[18px] border border-gray-200 bg-white/80 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Risk Surface</div>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900">{summary.risk_tagged_users}</div>
                  <div className="text-xs text-gray-500">占总用户 {formatPercent(summary.risk_tagged_users, summary.total_users)}</div>
                </div>
              </div>
              <div className="rounded-[18px] border border-gray-200 bg-white/80 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Review Throughput</div>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900">{reviewSuccessTotal}</div>
                  <div className="text-xs text-gray-500">已审核沉淀</div>
                </div>
              </div>
              <div className="rounded-[18px] border border-gray-200 bg-white/80 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Support Queue</div>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <div className="text-2xl font-semibold tabular-nums text-gray-900">{summary.pending_feedbacks}</div>
                  <div className="text-xs text-gray-500">待处理客服反馈</div>
                </div>
              </div>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-[1.3fr_0.9fr]">
            <PanelShell
              title="下载任务运行态"
              subtitle="把当前最需要盯住的下载任务、审核漏斗和排队项放在一起。"
              action={
                <Link to="/tasks" className="text-sm font-medium text-primary hover:text-primary/80">
                  查看任务列表
                </Link>
              }
            >
              <div className="grid gap-5 xl:grid-cols-[1.25fr_0.95fr]">
                <div className="space-y-3">
                  {data.active_download_tasks.length > 0 ? (
                    data.active_download_tasks.map((task) => <ActiveTaskRow key={task.id} task={task} />)
                  ) : (
                    <EmptyState text="当前没有进行中的下载任务。" />
                  )}
                </div>

                <div className="rounded-[22px] border border-gray-100 bg-gray-50/60 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">审核漏斗</h3>
                      <p className="mt-1 text-xs text-gray-500">按下载任务审核状态分布展示。</p>
                    </div>
                    <Link to="/assignments?taskType=download" className="text-xs font-medium text-primary hover:text-primary/80">
                      去审核
                    </Link>
                  </div>

                  <div className="mt-5 space-y-4">
                    {reviewFunnel.map((item) => (
                      <div key={item.label}>
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-gray-600">{item.label}</span>
                          <span className="font-medium tabular-nums text-gray-900">{item.value}</span>
                        </div>
                        <div className="h-2 rounded-full bg-white">
                          <div
                            className={`h-full rounded-full ${item.color}`}
                            style={{ width: `${(item.value / maxFunnelValue) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900">最近待审核</h3>
                    <div className="mt-3 space-y-2">
                      {data.pending_download_reviews.length > 0 ? (
                        data.pending_download_reviews.slice(0, 4).map((item) => (
                          <DownloadReviewItem key={item.assignment_id} item={item} />
                        ))
                      ) : (
                        <EmptyState text="当前没有待审核的下载提交。" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </PanelShell>

            <PanelShell
              title="待处理队列"
              subtitle="提现与客服问题放在同侧，方便运营快速切入。"
              action={
                <Link to="/withdrawals" className="text-sm font-medium text-primary hover:text-primary/80">
                  处理提现
                </Link>
              }
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-[20px] border border-gray-100 bg-gray-50/70 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Withdrawals</div>
                      <div className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">{summary.pending_withdrawals}</div>
                    </div>
                    <Link to="/withdrawals" className="text-xs font-medium text-primary hover:text-primary/80">
                      去处理
                    </Link>
                  </div>
                  <div className="mt-4 space-y-2">
                    {data.pending_withdrawals.length > 0 ? (
                      data.pending_withdrawals.slice(0, 3).map((item) => (
                        <PendingWithdrawalRow key={item.id} item={item} />
                      ))
                    ) : (
                      <EmptyState text="当前没有待审核提现。" />
                    )}
                  </div>
                </div>

                <div className="rounded-[20px] border border-gray-100 bg-gray-50/70 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Support Feedback</div>
                      <div className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">{summary.pending_feedbacks}</div>
                    </div>
                    <Link to="/support-feedback" className="text-xs font-medium text-primary hover:text-primary/80">
                      去处理
                    </Link>
                  </div>
                  <div className="mt-4 space-y-2">
                    {data.pending_feedbacks.length > 0 ? (
                      data.pending_feedbacks.slice(0, 3).map((item) => (
                        <PendingFeedbackRow key={item.id} item={item} />
                      ))
                    ) : (
                      <EmptyState text="当前没有待处理反馈。" />
                    )}
                  </div>
                </div>
              </div>
            </PanelShell>
          </div>

          <PanelShell
            title="新用户流入"
            subtitle="最近注册用户、Apple 验证状态与风险命中情况。"
            action={
              <Link to="/users" className="text-sm font-medium text-primary hover:text-primary/80">
                查看全部用户
              </Link>
            }
          >
            <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-[22px] border border-gray-100 bg-gray-50/60 p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-gray-400">Fresh Registrations</div>
                <div className="mt-3 text-4xl font-semibold tabular-nums text-gray-900">{summary.new_users_today}</div>
                <p className="mt-2 text-sm text-gray-500">今天新增注册用户，实时按上海时区自然日统计。</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[16px] border border-white bg-white px-4 py-3">
                    <div className="text-xs text-gray-400">Apple 已验证</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-gray-900">{summary.verified_users}</div>
                  </div>
                  <div className="rounded-[16px] border border-white bg-white px-4 py-3">
                    <div className="text-xs text-gray-400">风险命中用户</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums text-gray-900">{summary.risk_tagged_users}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {data.recent_users.length > 0 ? (
                  data.recent_users.map((item) => <RecentUserRow key={item.id} item={item} />)
                ) : (
                  <EmptyState text="当前没有最近注册用户数据。" />
                )}
              </div>
            </div>
          </PanelShell>
        </>
      ) : null}
    </div>
  );
}
